/**
 * Ячейки продаж таблицы размеров: уходимость, финотчёт WB и витрина Ozon.
 * Подсказки и типы блоков — `lib/product/cellTitles.ts`; остальные ячейки —
 * `cells.tsx`. Прежний модуль `components/sizeCells.tsx` — фасад.
 */

import { nf, pct } from "@/lib/format";
import {
  type OzonBlock,
  type ReportBlock,
  type SpeedWindow,
  ozonTitle,
  reportTitle,
  speedTitle,
} from "@/lib/product/cellTitles";
import { reportCellProps } from "@/components/sizes/cells";

/** «Продадим за N дней» — чистый выкуп за окно. Число буквальное: горизонт
 * равен окну, поэтому «продадим за 60» численно равно «продали за прошлые
 * 60». Модели прогноза здесь нет, и подсказка говорит это прямым текстом. */
export function SpeedCell({
  cell,
  days,
}: {
  cell: SpeedWindow | undefined;
  days: number;
}) {
  return (
    <td
      title={speedTitle(cell, days)}
      className="border-b border-border px-1.5 py-1 text-right tabular-nums"
    >
      {cell ? nf.format(cell.net) : <span className="text-fg-faint">—</span>}
    </td>
  );
}

/** Половина «продадим» — ОДНА площадка.
 *
 * Стоит подстрочником под суммой, тем же приёмом, каким на витрине доли стоят
 * под своими числами: приглушена и с отступом, потому что РАСКЛАДЫВАЕТ строку
 * над собой, а не продолжает таблицу вниз. До 11.09.26 слагаемые жили только в
 * подсказке суммы, и «14 шт за 30 дней» не отвечало на вопрос закупщика, какая
 * площадка их увозит: у 22741 остаток разошёлся между WB и Ozon в тринадцать
 * раз, а скорость смотрелась одним числом.
 *
 * ПРОЧЕРК ЗНАЧИТ ТО ЖЕ, ЧТО У СУММЫ, — движений размера за окно не было вовсе.
 * Своего прочерка «этой площадки у размера нет» половина не рисует: сервер
 * отдаёт её нулём, и отличить «там не продавалось» от «там не заводилось»
 * нечем. Ноль здесь честен — товар на площадке ЕСТЬ, иначе строки не было бы
 * вообще (её рисуют только у товара с обеими площадками).
 */
export function SpeedHalfCell({
  cell,
  half,
  days,
  platform,
}: {
  cell: SpeedWindow | undefined;
  half: "wb" | "ozon";
  days: number;
  /** Имя площадки для подсказки: «WB» / «Ozon». */
  platform: string;
}) {
  const part = cell?.[half];
  return (
    <td
      title={
        part
          ? `${platform}: продано ${nf.format(part.sold)},` +
            ` возврат ${nf.format(part.returned)}` +
            ` → чистый выкуп ${nf.format(part.net)} шт за ${days} дней` +
            ` · ${(part.net / days).toFixed(2)} шт/дн`
          : `движений по этому размеру за ${days} дней не было`
      }
      className="border-b border-border px-1.5 py-1 text-right tabular-nums text-fg-muted"
    >
      {part ? nf.format(part.net) : <span className="text-fg-faint">—</span>}
    </td>
  );
}

/** «Выкуплено» — ЧИСТОЕ число, продано минус возврат. Валовые штуки и
 * доставки остались в подсказке: с 11.09.26 у блока три строки, и своей
 * строки у них больше нет. */
export function ReportCell({
  cell,
  onOpen,
}: {
  cell: ReportBlock | undefined;
  onOpen?: () => void;
}) {
  const { className = "", ...rest } = reportCellProps(onOpen);
  return (
    <td
      title={reportTitle(cell)}
      className={`border-b border-border px-1.5 py-1 text-right tabular-nums ${className}`}
      {...rest}
    >
      {cell ? nf.format(cell.net) : <span className="text-fg-faint">—</span>}
    </td>
  );
}

/** «Возврат, %» — доля вернувшегося ОТ ПРОДАННОГО, а не от чистого выкупа:
 * вопрос строки «из того, что выкупили, сколько вернулось». У размера 2982
 * товара 22741 это 9 ÷ 25 = 36,0 %, а не 9 ÷ 16 = 56,3 %.
 *
 * НЕ ПОДСВЕЧИВАЕТСЯ ВОВСЕ, в отличие от соседки «выкуп, %»: у выкупа «больше —
 * лучше» очевидно, у возврата правило обратное, и второй цвет на той же
 * таблице потребовал бы второй легенды. Акцент на экране один.
 *
 * Одна ячейка на ОБЕ площадки — значение и подсказку даёт вызывающий: строка
 * на экране одна, и две копии разъехались бы форматом.
 */
export function ReturnPctCell({
  value,
  title,
  onOpen,
}: {
  value: number | null | undefined;
  title: string;
  onOpen?: () => void;
}) {
  const { className = "", ...rest } = reportCellProps(onOpen);
  return (
    <td
      title={title}
      className={`border-b border-border px-1.5 py-1 text-right tabular-nums text-fg-muted ${className}`}
      {...rest}
    >
      {value == null ? <span className="text-fg-faint">—</span> : pct(value)}
    </td>
  );
}

/** Ячейка Ozon — НЕМАЯ, в отличие от соседки по WB: панель справа рисует ряд
 * финотчёта Wildberries, и клик по числу Ozon открыл бы график, который с этим
 * числом не сходится. Тот же довод, по которому молчала «цена рейса». */
export function OzonCell({ cell }: { cell: OzonBlock | null | undefined }) {
  return (
    <td title={ozonTitle(cell)} className="border-b border-border px-1.5 py-1 text-right tabular-nums">
      {cell ? nf.format(cell.net) : <span className="text-fg-faint">—</span>}
    </td>
  );
}

/** «Выкуп, %» Ozon. Планка сравнения — СВОЙ итог площадки: у WB знаменатель
 * доставки, у Ozon заказы, и общая планка сравнивала бы доли от разных
 * оснований. */
export function OzonPctCell({
  cell,
  productPct,
}: {
  cell: OzonBlock | null | undefined;
  productPct: number | null;
}) {
  const better = cell?.pct != null && productPct != null && cell.pct > productPct;
  return (
    <td
      title={ozonTitle(cell)}
      className={`border-b border-border px-1.5 py-1 text-right tabular-nums ${
        better ? "font-semibold text-accent-text" : "text-fg-muted"
      }`}
    >
      {cell?.pct == null ? <span className="text-fg-faint">—</span> : pct(cell.pct)}
    </td>
  );
}

/** Правило подсветки то же, что у выкупа по заказам: размер сравнивается со
 * СВОИМ товаром, а не с абсолютной шкалой. */
export function ReportPctCell({
  cell,
  productPct,
  onOpen,
}: {
  cell: ReportBlock | undefined;
  productPct: number | null;
  onOpen?: () => void;
}) {
  const better =
    cell?.pct != null && productPct != null && cell.pct > productPct;
  const { className = "", ...rest } = reportCellProps(onOpen);
  return (
    <td
      title={reportTitle(cell)}
      className={`border-b border-border px-1.5 py-1 text-right tabular-nums ${
        better ? "font-semibold text-accent-text" : "text-fg-muted"
      } ${className}`}
      {...rest}
    >
      {cell?.pct == null ? (
        <span className="text-fg-faint">—</span>
      ) : (
        pct(cell.pct)
      )}
    </td>
  );
}
